import Core            from '@/core/Core';
import DemandsDAO      from '@/domains/Demand/DemandsDAO';
import Demand          from '@/domains/Demand/Demand';
import ProductsService from '@/domains/Products/ProductsService';
import TendersService  from '@/domains/Tenders/TendersService';
import ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

class DemandsService {

  /**
   * Ищет товары по артикулам покупателя.
   * Возвращает сырые записи бэка: productid / productcode / productname.
   */
  static async findProductsByCodes(codes: string[]): Promise<any[]> {
    const response = await DemandsDAO.findProductsByCodes(codes);
    return Core.utils.isNotEmptyArray(response.data) ? response.data : [];
  }

  /**
   * Загружает список потребностей.
   * Для использования вне реестра (диалоги, автокомплиты).
   */
  static async getList(
    params?: Parameters<typeof DemandsDAO.getList>[0]
  ): Promise<Demand[]> {
    return (await this.getListPaginated(params)).data;
  }

  /**
   * Загружает список потребностей с пагинацией.
   * Используется в реестре через DataGridLoader.
   */
  static async getListPaginated(
    params?: Parameters<typeof DemandsDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort
    }
  ): Promise<{ data: Demand[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await DemandsDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Demand.fromServer) as Demand[],
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загружает одну потребность по ID
   */
  static async get(id: number): Promise<Demand> {
    const response = await DemandsDAO.get(id);
    return Core.utils.hydrate(response.data, Demand.fromServer) as Demand;
  }

  /**
   * Создаёт потребности
   */
  static async add(demands: Demand[]): Promise<boolean> {
    const response = await DemandsDAO.add(demands);

    if (response.ok) {
      Core.showNotification.success('Потребности успешно созданы');
    }

    return response.ok;
  }

  /**
   * Обновляет потребность
   */
  static async update(demand: Demand): Promise<boolean> {
    const response = await DemandsDAO.update(demand);

    if (response.ok) {
      Core.showNotification.success('Потребность успешно обновлена');
    }

    return response.ok;
  }

  /**
   * Удаляет потребность по ID
   */
  static async delete(id: number): Promise<boolean> {
    const response = await DemandsDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Потребность успешно удалена');
    }

    return response.ok;
  }

  /**
   * Удаляет потребность с подтверждением. Возвращает true, если удаление произошло —
   * вызывающий сам решает что делать (обычно refresh лоадера реестра).
   * При успехе помечает потребность удалённой — по этой отметке карточка закрывается.
   */
  static async deleteWithConfirm(demand: Demand): Promise<boolean> {
    const confirmed = await Core.confirm(
      'Подтверждаете удаление потребности?',
      `Потребность: ${demand.product.name}`,
    );

    if (confirmed) {
      const isDeleted = await this.delete(demand.id);

      if (isDeleted === true) {
        demand.isDeleted = true;
      }

      return isDeleted;
    } else {
      return false;
    }
  }

  /**
   * Массовое удаление потребностей: подтверждение, параллельный delete,
   * сводное уведомление по успешным и упавшим. Возвращает разнесённые списки.
   */
  static async deleteMany(demands: Demand[]): Promise<{ success: Demand[]; failed: Demand[] }> {
    const success: Demand[] = [];
    const failed:  Demand[] = [];

    if (demands.length === 0) return { success, failed };

    const confirmed = await Core.confirm('Вы уверены, что хотите удалить выбранные потребности?');
    if (confirmed === false) return { success, failed };

    const settled = await Promise.allSettled(
      demands.map(_demand => DemandsDAO.delete(_demand.id)),
    );

    settled.forEach((result, index) => {
      const okay = result.status === 'fulfilled' && result.value.ok === true;
      (okay ? success : failed).push(demands[index]);
    });

    if (success.length > 0) {
      Core.showNotification.success(
        `Успешно удалено ${success.length} ${
          Core.utils.regional.ru.pluralize(success.length, 'потребность', 'потребности', 'потребностей')
        }`,
      );
    }

    if (failed.length > 0) {
      const names = failed.map(_demand => _demand.product.name).join(', ');
      Core.showNotification.error(
        `Не удалось удалить ${failed.length} ${
          Core.utils.regional.ru.pluralize(failed.length, 'потребность', 'потребности', 'потребностей')
        }: ${names}`,
      );
    }

    return { success, failed };
  }

  /**
   * Открывает диалог массового создания потребностей (на заказ или предзаказ)
   */
  static showOrderCreateDialog(isPreorder: boolean, onSaved: () => void): void {
    Core.dialogs.DemandOrderCreateDialog.show(
      { isPreorder },
      { save: onSaved },
    );
  }

  /**
   * Открывает карточку потребности.
   * Промис резолвится по закрытию карточки — вызывающий сам решает, рефрешить ли
   * список (как в реестре по двойному клику и в пункте «Открыть» контекстного меню).
   */
  static showDemandCard(demand: Demand | null): Promise<boolean> {
    return Core.dialogs.DemandCard.showAsPromise({ id: demand?.id });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Пункты контекстного меню потребности для грида реестра.
   * Передаётся в VDataGridV2 пропом :context-menu — грид сам рисует
   * кебаб-колонку и меню по правому клику, и обновляет список после
   * действия, только если callback пункта вернул true.
   *
   * callback возвращает промис — грид дождётся завершения перед refresh.
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (demand: Demand) => { return this.showDemandCard(demand); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Сравнить предложения',
        'difference',
        (demand: Demand) => { return Core.dialogs.OrderOptionsComparisonDialog.showAsPromise({ demandId: demand.id }); },
        (demand: Demand) => { return demand.getReasonCannotCompareOffers(); },
        () => { return Core.currentPartner?.isBuyer === false; },
      ),
      new ContextMenuItem(
        'Создать тендер',
        'gavel',
        (demand: Demand) => {
          return Core.dialogs.TenderCreateCard.showAsPromise({
            demand: demand,
            isFlexible: false
          });
        },
        (demand: Demand) => { return demand.getReasonCannotCreateTender(); },
      ),
      new ContextMenuItem(
        'Создать тендер котировок',
        'gavel',
        (demand: Demand) => {
          return Core.dialogs.TenderCreateCard.showAsPromise({
            demand: demand,
            isFlexible: true
          });
        },
        (demand: Demand) => { return demand.getReasonCannotCreateTender(); },
      ),
      new ContextMenuItem(
        'Открыть тендер',
        'open_in_new',
        (demand: Demand) => { TendersService.showTenderCard(demand.tender); },
        (demand: Demand) => { return demand.getReasonCannotShowTender(); },
      ),
      new ContextMenuItem(
        'Открыть товар',
        'open_in_new',
        (demand: Demand) => { ProductsService.showCard(demand.product.id); },
      ),
      new ContextMenuItem(
        'История потребности',
        'history',
        (demand: Demand) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'demand', entityId: demand.id, showAddComment: false }); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (demand: Demand) => { return this.deleteWithConfirm(demand); },
        (demand: Demand) => { return demand.getReasonCannotDelete(); },
      ),
    ];
  }
}

export default DemandsService;